/**
 * Overage Details Card
 *
 * Displays overage payment information when a booking exceeds subscription limits.
 * Shows explanation, usage bars for active limits, overage visualization, and charges breakdown.
 *
 * @module butterUI/components/OverageDetailsCard
 */

import { ErrorIcon } from '#icons';
import { useState } from 'react';
import type { OverageLimitType } from '@lgc/database/types';
import { formatHoursDisplay } from '@butter/library/shared';

export interface UsageInfo {
  /** Minutes used today */
  minutesToday: number;
  /** Minutes used this week */
  minutesThisWeek: number;
  /** Minutes used this month */
  minutesThisMonth: number;
  /** Active/concurrent minutes used */
  activeMinutes: number;
  /** Maximum minutes per day (null = unlimited) */
  maxMinutesPerDay: number | null;
  /** Maximum minutes per week (null = unlimited) */
  maxMinutesPerWeek: number | null;
  /** Maximum minutes per month (null = unlimited) */
  maxMinutesPerMonth: number | null;
  /** Maximum active/concurrent minutes (null = unlimited) */
  maxActiveMinutes: number | null;
  /** Remaining minutes today */
  remainingMinutesToday: number | null;
  /** Remaining minutes this week */
  remainingMinutesThisWeek: number | null;
  /** Remaining minutes this month */
  remainingMinutesThisMonth: number | null;
  /** Remaining active minutes */
  remainingActiveMinutes: number | null;
  /** Session slots currently held — a COUNT, not minutes. */
  activeBookings?: number;
  /** Maximum concurrent sessions (null/absent = unlimited) */
  maxActiveSessions?: number | null;
  /** Remaining session slots (null = unlimited) */
  remainingActiveSessions?: number | null;
}

export interface OverageInfo {
  /** Whether booking has overage */
  hasOverage: boolean;
  /** Minutes that exceed limits */
  overageMinutes: number;
  /** Minutes covered by subscription */
  coveredMinutes: number;
  /** Type of limit causing overage */
  limitType: OverageLimitType;
  /** Remaining minutes in the exceeded limit */
  remainingMinutes: number;
}

export interface OverageDetailsCardProps {
  /** Overage explanation message */
  message: string;
  /** Total booking duration in minutes */
  bookingDurationMinutes: number;
  /** Usage information for the user's subscription */
  usage?: UsageInfo;
  /** Overage information */
  overage?: OverageInfo;
  /** Optional details about what caused the overage (e.g., "30 minutes over limit") */
  overageDetails?: string;
  /**
   * Test hook for the card root. Declared explicitly because this component does
   * not spread rest props — a caller passing `data-testid` was silently dropped,
   * leaving the e2e locator for this card unable to match anything.
   */
  'data-testid'?: string;
  /** Subscription name (e.g., "Gold Individual Usage") */
  subscriptionName?: string;
  /**
   * Team membership: the usage pool is shared by the whole roster, so the
   * numbers may reflect teammates' bookings, not the viewer's. Switches the
   * limit labels to the "Shared X" variants (same wording as
   * SubscriptionUsagePanel's `shared` prop) and adds the pooled-usage note.
   */
  shared?: boolean;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Minutes as "1.5h" from an hour up, "45min" below. The hours side is the shared
 * display rule: it used to glue the leftover minutes on as tenths, so 117 min
 * (1.95 h) read "1.10h".
 */
export function minutesToHoursDisplay(minutes: number): string {
  return minutes >= 60 ? formatHoursDisplay(minutes, { decimals: 2 }) : `${minutes}min`;
}

/**
 * Usage Bar Component
 * Shows visual progress bar for a single usage limit
 */
interface UsageBarProps {
  label: string;
  usedMinutes: number;
  maxMinutes: number;
  remainingMinutes: number | null;
  bookingMinutes: number;
  /**
   * What the numbers mean. 'sessions' rows carry plain counts in the same
   * fields — the bar geometry is identical, only the formatter differs.
   */
  unit?: 'hours' | 'sessions';
}

// Striped fill used for the "booking" / "overage" portions (light brand purple).
const STRIPE_FILL =
  'repeating-linear-gradient(45deg, rgba(127, 86, 217, 0.18), rgba(127, 86, 217, 0.18) 4px, rgba(127, 86, 217, 0.38) 4px, rgba(127, 86, 217, 0.38) 8px)';

function UsageBar({ label, usedMinutes, maxMinutes, remainingMinutes, bookingMinutes, unit = 'hours' }: UsageBarProps) {
  // A session is indivisible: the booking consumes exactly one slot regardless
  // of its duration, so the "with booking" segment is 1, never a minute count.
  const bookingAmount = unit === 'sessions' ? 1 : bookingMinutes;
  const fmt = (value: number) => (unit === 'sessions' ? String(value) : minutesToHoursDisplay(value));

  // Clamp the denominator, don't divide by the raw max — the same guard the
  // shared `UsageBar` (components/data-display/UsageBar) uses, for the same
  // reason. A pool may legitimately be capped at 0: a membership including no
  // time, where every booking bills as overage. Dividing by it gives `0/0 → NaN`
  // when nothing is used, which reaches `style={{ width: 'NaN%' }}` and renders
  // an empty track; and `n/0 → Infinity` once anything is, which clamps to 100
  // and renders a FULL bar labelled "0h / 0h used". A `Number.isFinite` guard
  // would catch only the first. With the clamp, a zero pool draws as entirely
  // this-booking, which is exactly what it is.
  const capacity = maxMinutes > 0 ? maxMinutes : Math.max(usedMinutes + bookingAmount, 1);
  const usedPercent = Math.min((usedMinutes / capacity) * 100, 100);
  const bookingPercent = Math.min((bookingAmount / capacity) * 100, 100);

  return (
    <div className="rounded-2xl border border-gray-200 p-5">
      {/* Label and "left" pill */}
      <div className="mb-4 flex items-center justify-between">
        <span className="text-base font-semibold text-gray-900">{label}</span>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600">
          {fmt(remainingMinutes || 0)} left
        </span>
      </div>

      {/* Progress Bar */}
      <div className="relative h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
        {/* Used portion (filled) */}
        <div
          className="absolute left-0 top-0 h-full bg-brand-600 transition-all rounded-full"
          style={{ width: `${usedPercent}%` }}
        />

        {/* Booking portion (striped) */}
        <div
          className="absolute top-0 h-full transition-all"
          style={{
            left: `${usedPercent}%`,
            width: `${Math.min(bookingPercent, 100 - usedPercent)}%`,
            backgroundImage: STRIPE_FILL,
            borderTopRightRadius: usedPercent + bookingPercent >= 100 ? '9999px' : '0',
            borderBottomRightRadius: usedPercent + bookingPercent >= 100 ? '9999px' : '0',
          }}
        />
      </div>

      {/* Usage Text */}
      <div className="mt-4 flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-brand-600"></div>
          <span className="text-gray-600">
            {fmt(usedMinutes)} / {fmt(maxMinutes)} used
          </span>
        </div>
        {bookingAmount > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="size-2 rounded-full" style={{ backgroundImage: STRIPE_FILL }}></div>
            <span className="text-gray-600">
              +{fmt(bookingAmount)} with booking
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Overage Details Card Component
 *
 * Displays overage payment information with alert styling.
 * Shows usage bars for active limits, overage visualization, and breakdown of additional charges.
 * Follows mobile-first responsive design and WCAG 2.1 AA accessibility.
 *
 * @example
 * ```tsx
 * <OverageDetailsCard
 *   message="Your date and time exceeds your subscription limits. This additional fee covers the overage period above your included minutes."
 *   bookingDurationMinutes={120}
 *   usage={usageInfo}
 *   overage={overageInfo}
 *   subscriptionName="Gold Individual Usage"
 * />
 * ```
 */
export function OverageDetailsCard({
  message,
  bookingDurationMinutes,
  usage,
  overage,
  subscriptionName,
  shared = false,
  className = '',
  'data-testid': testId,
}: OverageDetailsCardProps) {
  const [showDetails, setShowDetails] = useState(false);

  // Define the limit interface
  interface UsageLimit {
    label: string;
    usedMinutes: number;
    maxMinutes: number;
    remainingMinutes: number | null;
    /** 'sessions' rows carry counts in the *Minutes fields. Defaults to hours. */
    unit?: 'hours' | 'sessions';
  }

  // Build list of active limits (only show limits that are set, not null/unlimited)
  const activeLimits: UsageLimit[] = [];
  if (usage) {
    if (usage.maxMinutesPerDay !== null) {
      activeLimits.push({
        label: shared ? 'Shared Daily Hours' : 'Daily Hours',
        usedMinutes: usage.minutesToday,
        maxMinutes: usage.maxMinutesPerDay,
        remainingMinutes: usage.remainingMinutesToday,
      });
    }
    if (usage.maxMinutesPerWeek !== null) {
      activeLimits.push({
        label: shared ? 'Shared Weekly Hours' : 'Weekly Hours',
        usedMinutes: usage.minutesThisWeek,
        maxMinutes: usage.maxMinutesPerWeek,
        remainingMinutes: usage.remainingMinutesThisWeek,
      });
    }
    if (usage.maxMinutesPerMonth !== null) {
      activeLimits.push({
        label: shared ? 'Shared Monthly Hours' : 'Monthly Hours',
        usedMinutes: usage.minutesThisMonth,
        maxMinutes: usage.maxMinutesPerMonth,
        remainingMinutes: usage.remainingMinutesThisMonth,
      });
    }
    if (usage.maxActiveMinutes !== null) {
      activeLimits.push({
        label: shared ? 'Shared Active Hours' : 'Active Booking Hours',
        usedMinutes: usage.activeMinutes,
        maxMinutes: usage.maxActiveMinutes,
        remainingMinutes: usage.remainingActiveMinutes,
      });
    }
    // Sessions are a COUNT — the bar below is unit-agnostic (it only needs
    // used/max/remaining in the same unit), but the label must not say "Hours".
    if (usage.maxActiveSessions != null) {
      activeLimits.push({
        label: shared ? 'Shared Active Sessions' : 'Active Sessions',
        usedMinutes: usage.activeBookings ?? 0,
        maxMinutes: usage.maxActiveSessions,
        remainingMinutes: usage.remainingActiveSessions ?? null,
        unit: 'sessions',
      });
    }
  }

  return (
    <div
      className={`rounded-2xl border border-gray-200 bg-white p-6 shadow-sm ${className}`}
      role="region"
      aria-labelledby="overage-title"
      data-testid={testId}
    >
      {/* Header with Info Icon */}
      <div className="mb-4 flex items-start gap-3">
        <div
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50"
          aria-hidden="true"
        >
          <ErrorIcon className="size-5 text-blue-600" />
        </div>

        <div className="flex-1">
          <h3
            id="overage-title"
            className="text-base font-semibold text-blue-600"
          >
            Overage payment required
          </h3>
        </div>
      </div>

      {/* Explanation Message */}
      <p className={`${shared ? 'mb-2' : 'mb-6'} text-sm text-gray-700 leading-relaxed`}>
        {message}
      </p>

      {/* Team pool note — the usage below may have been consumed by teammates,
          and without this line an overage looks like an accounting error to a
          member who hasn't booked recently. */}
      {shared && (
        <p className="mb-6 text-sm text-gray-500 leading-relaxed">
          Your membership&rsquo;s usage is shared across your whole team, so these
          totals include your teammates&rsquo; bookings.
        </p>
      )}

      {/* Overage Visualization Bar (if overage data provided) */}
      {overage && overage.hasOverage && (
        <div className="mb-6 space-y-3">
          {/* Progress Bar */}
          <div className="relative h-3 w-full bg-gray-100 rounded-full overflow-hidden">
            {/* Covered portion */}
            <div
              className="absolute left-0 top-0 h-full bg-brand-600 transition-all rounded-full"
              style={{ width: `${(overage.coveredMinutes / bookingDurationMinutes) * 100}%` }}
            />

            {/* Overage portion (striped pattern) */}
            <div
              className="absolute top-0 h-full transition-all"
              style={{
                left: `${(overage.coveredMinutes / bookingDurationMinutes) * 100}%`,
                width: `${(overage.overageMinutes / bookingDurationMinutes) * 100}%`,
                backgroundImage: STRIPE_FILL,
                borderTopRightRadius: '9999px',
                borderBottomRightRadius: '9999px'
              }}
            />
          </div>

          {/* Legend */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-full bg-brand-600"></div>
              <span className="text-gray-600">
                {minutesToHoursDisplay(overage.coveredMinutes)} covered by membership
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="size-2.5 rounded-full" style={{ backgroundImage: STRIPE_FILL }}></div>
              <span className="text-gray-700 font-medium">
                {minutesToHoursDisplay(overage.overageMinutes)} overage
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Required Message */}
      <div className="mb-6 border-l-2 border-brand-600 pl-4">
        <p className="text-sm text-gray-600">
          Payment method required for overage charges.
        </p>
      </div>

      {/* Collapsible Usage Details Section */}
      {usage && activeLimits.length > 0 && subscriptionName && (
        <div>
          {/* Toggle Button */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex w-full items-center justify-between text-left transition-colors group"
            aria-expanded={showDetails}
            aria-controls="usage-details"
          >
            <span className="text-lg font-semibold text-gray-900">{subscriptionName} Usage Details</span>
            <svg
              className={`size-5 shrink-0 text-gray-500 transition-transform group-hover:text-gray-900 ${showDetails ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Collapsible Content */}
          {showDetails && (
            <div
              id="usage-details"
              className="mt-4 space-y-4"
            >
              {activeLimits.map((limit, index) => (
                <UsageBar
                  key={index}
                  label={limit.label}
                  usedMinutes={limit.usedMinutes}
                  maxMinutes={limit.maxMinutes}
                  remainingMinutes={limit.remainingMinutes}
                  bookingMinutes={bookingDurationMinutes}
                  unit={limit.unit}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
